import React, { useContext, useState } from 'react';
import {
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Box,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  gray100,
  gray80,
  secondaryButtonBackgroundColor,
  tertiaryButtonBackgroundColor,
  white,
} from '../../constants/colors';
import CustomSnackbar from './CustomSnackbar';
import DeleteModal from './DeleteModal';
import ReSendInviteMailModal from './ReSendInviteMailModal';
import { deleteUser } from '../../api/firebase/firestore/user';
import { FirebaseError } from 'firebase/app';
import { getErrorMessageFromAuthErrorCode } from '../../api/firebase/auth/error';
import { adminCountError, authError, common } from '../../modules/error';
import { userRole, registerStatus } from '../../constants/enum';
import CustomLoading from './CustomLoading';
import { AuthContext } from '../../contexts/auth';
import { sendSignInLinkToEmail } from 'firebase/auth';
import { actionCodeSettings, auth } from '../../api/firebase/config';
import NoAccountIcon from '../assets/Account circle.svg';
import { useAccess } from '../../contexts/index';
import { UserType } from '../../validation/user/userType';
import type { GroupType } from '../../validation/group/groupType';

type StaffListCardProps = {
  isAdmin: boolean;
  staffList: UserType[];
  loginUser: UserType | null;
  groups?: GroupType[];
};

const styles = {
  paper: {
    padding: '20px',
    width: '100%',
    backgroundColor: white,
    border: '1px solid #E0E0E0',
    overflowY: 'auto',
    paddingBottom: '220px',
    '@media (max-width: 600px)': {
      paddingBottom: '0',
    },
  },
  tableContainer: {
    minWidth: '900px',
    overflowX: 'auto',
  },
  tableHeader: {
    color: gray80,
    fontSize: '14px',
    fontWeight: 400,
  },
  tableCell: {
    padding: '6.5px',
    color: gray100,
    fontSize: '14px',
    fontWeight: 400,
  },
  avatar: {
    width: 32,
    height: 32,
    marginRight: '8px',
  },
  buttonHeight: {
    height: '32px',
  },
  deleteButton: {
    marginLeft: '16px',
  },
  memberText: {
    color: gray80,
    fontSize: '14px',
    fontWeight: 400,
  },
  modalCloseButton: {
    cursor: 'pointer',
    height: '32px',
    width: '32px',
  },
  modalTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '40px 40px 0 40px',
  },
  modalTitleText: {
    fontSize: '22px',
    fontWeight: 400,
    '@media (max-width: 600px)': {
      fontSize: '20px',
    },
    '@media (max-width: 320px)': {
      fontSize: '18px',
    },
  },
  dialogContent: {
    padding: '24px 40px 0 40px',
  },
  modalContentText: {
    fontSize: '16px',
    fontWeight: 400,
    '@media (max-width: 320px)': {
      fontSize: '14px',
    },
  },
  dialogActions: {
    display: 'flex',
    justifyContent: 'center',
    padding: '32px 40px 40px 40px',
  },
  actionButton: {
    fontSize: '16px',
    fontWeight: 700,
    width: '100%',
    '@media (max-width: 600px)': {
      fontSize: '14px',
    },
    '@media (max-width: 320px)': {
      fontSize: '12px',
    },
  },
};

const getRoleText = (staffRole: number): string => {
  return staffRole === userRole.admin ? '管理者' : 'メンバー';
};

const tableHeaders = [
  '担当者名',
  '部署',
  '権限',
  '所属グループ',
  'メールアドレス',
  '1日の上限',
  '',
];

const StaffListCard: React.FC<StaffListCardProps> = ({
  isAdmin,
  staffList,
  loginUser,
  groups = [],
}) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [selectedEmail, setSelectedEmail] = useState<string>('');
  const [selectedDeleteUid, setSelectedDeleteUid] = useState<string>('');
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [infoMessage, setInfoMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const { user } = useContext(AuthContext);
  const loginUserUid = user?.uid;
  const { allowAccess } = useAccess();

  const handleOpen = (email: string): void => {
    setSelectedEmail(email);
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
    setSelectedEmail('');
  };

  const handleDeleteUser = async (): Promise<void> => {
    setLoading(true);
    // 管理者ユーザー数をカウント
    const adminCount = staffList.filter(staff => staff.role === userRole.admin).length;
    const existUser = staffList.filter(staff => staff.uid === selectedDeleteUid).length;

    if (adminCount <= 1) {
      // 管理者が0人になる場合はエラーメッセージを表示
      setSnackbarMessage(adminCountError);
      setSnackbarOpen(true);
      setLoading(false);
      return;
    }

    if (existUser < 1) {
      setSnackbarMessage(authError.userNotFound);
      setSnackbarOpen(true);
      setLoading(false);
      return;
    }

    try {
      if (!loginUserUid) throw new Error('ログインしてください。');
      const targetUser = staffList.find(staff => staff.uid === selectedDeleteUid);
      if (!targetUser) {
        return;
      }

      const { registerStatus: userRegisterStatus } = targetUser;

      // ユーザー削除処理
      await deleteUser(selectedDeleteUid, loginUserUid);

      if (userRegisterStatus === registerStatus.emailRegistered) {
        navigate('/staff/invite/cancel');
      } else if (userRegisterStatus === registerStatus.finished) {
        navigate('/staff/delete/complete');
      } else {
        return;
      }
    } catch (error: unknown) {
      let errorText: string;

      if (error instanceof FirebaseError) {
        errorText = getErrorMessageFromAuthErrorCode(error.code);
      } else {
        errorText = common;
      }

      setLoading(false);
      setSnackbarMessage(errorText);
      setSnackbarOpen(true);
      return;
    }
  };

  const resendEmail = async (): Promise<void> => {
    setLoading(true);
    try {
      await sendSignInLinkToEmail(auth, selectedEmail, actionCodeSettings(selectedEmail));
      handleClose();
      setLoading(false);
      setSnackbarOpen(true);
      setInfoMessage('招待メールを再送しました。');
    } catch (error) {
      setLoading(false);
      setSnackbarMessage('メールの送信に失敗しました。');
      setSnackbarOpen(true);
    }
  };

  return (
    <Paper elevation={0} sx={styles.paper}>
      <CustomLoading loading={loading} />
      <TableContainer sx={styles.tableContainer}>
        <Table>
          <TableHead>
            <TableRow>
              {tableHeaders.map((header, index) => (
                <TableCell sx={styles.tableHeader} key={index}>
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {staffList.map(staff => (
              <TableRow key={staff.uid}>
                <TableCell sx={styles.tableCell}>
                  <Box display="flex" alignItems="center">
                    <Avatar sx={styles.avatar} src={staff.iconUrl ?? NoAccountIcon} />
                    <Typography sx={styles.tableCell}>{`${staff.name ?? ''}`}</Typography>
                  </Box>
                </TableCell>
                <TableCell sx={styles.tableCell}>{staff.department}</TableCell>
                <TableCell sx={styles.tableCell}>
                  {getRoleText(staff.role ?? userRole.member)}
                </TableCell>
                <TableCell sx={styles.tableCell}>
                  {(() => {
                    const memberGroups = groups
                      .filter(g => g.memberUids.includes(staff.uid))
                      .map(g => g.name);
                    const pendingGroups = (staff.pendingGroupIds ?? [])
                      .map(id => groups.find(g => g.groupId === id))
                      .filter((g): g is GroupType => g !== undefined)
                      .filter(g => !g.memberUids.includes(staff.uid))
                      .map(g => `${g.name}（招待中）`);
                    const allGroups = [...memberGroups, ...pendingGroups];
                    return allGroups.length > 0 ? allGroups.join(', ') : '-';
                  })()}
                </TableCell>
                <TableCell sx={styles.tableCell}>{staff.email}</TableCell>
                <TableCell sx={styles.tableCell}>
                  {staff.daily_limit != null ? `${staff.daily_limit}件` : '無制限'}
                </TableCell>
                <TableCell align="right" sx={styles.tableCell}>
                  {!isAdmin && loginUser?.uid !== staff.uid ? (
                    <Typography sx={styles.memberText}>編集権限がありません</Typography>
                  ) : staff.registerStatus === registerStatus.finished ? (
                    <Button
                      sx={[secondaryButtonBackgroundColor, styles.buttonHeight]}
                      onClick={(): void => {
                        allowAccess('/staff/edit');
                        navigate('/staff/edit', { state: { id: staff.uid, isAdmin } });
                      }}
                    >
                      編集
                    </Button>
                  ) : (
                    <Button
                      sx={[secondaryButtonBackgroundColor, styles.buttonHeight]}
                      onClick={(): void => handleOpen(staff.email)}
                    >
                      招待メールを再送
                    </Button>
                  )}
                  {isAdmin ? (
                    <Button
                      onClick={(): void => {
                        setSelectedDeleteUid(staff.uid);
                        setDeleteModalOpen(true);
                      }}
                      sx={[tertiaryButtonBackgroundColor, styles.deleteButton, styles.buttonHeight]}
                    >
                      削除
                    </Button>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <ReSendInviteMailModal
        open={open}
        onClose={(): void => handleClose()}
        onSend={resendEmail}
        title={selectedEmail}
      />
      <DeleteModal
        open={deleteModalOpen}
        onClose={(): void => setDeleteModalOpen(false)}
        onDelete={handleDeleteUser}
        title="担当者"
      />

      <CustomSnackbar
        autoHideDuration={6000}
        open={snackbarOpen}
        onClose={(): void => setSnackbarOpen(false)}
        text={snackbarMessage}
        infoText={infoMessage}
      />
    </Paper>
  );
};

export default StaffListCard;
